import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { ShieldAlert, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const BLOCKER_LABELS = {
  "induction.missing": "Induction not completed",
  "induction.expired": "Induction expired",
  "insurance.missing": "Insurance certificate missing",
  "insurance.expired": "Insurance certificate expired",
  "rams.unaccepted": "RAMS not accepted (or new version pending)",
  "dbs.missing": "DBS card missing",
  "dbs.expired": "DBS card expired",
  "cscs.missing": "CSCS card missing",
  "cscs.expired": "CSCS card expired",
  "permit.required_but_none_active": "Required permit-to-work missing",
};

export default function ContractorOverrides() {
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/contractor-overrides/pending");
      setPending(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { refresh(); const t = setInterval(refresh, 15000); return () => clearInterval(t); }, []);

  const approve = async (req) => {
    const justification = window.prompt("Justification (≥ 20 chars):", "");
    if (!justification || justification.length < 20) {
      return toast.error("Justification must be at least 20 characters");
    }
    setBusy(req.id);
    try {
      const { data } = await api.post("/visits/contractor-sign-in-override", {
        contractor_id: req.contractor_id,
        site_id: req.site_id,
        blockers_acknowledged: (req.blockers || []).map((b) => b.code),
        justification,
        valid_for_minutes: 30,
      });
      navigator.clipboard?.writeText(data.override_token).catch(() => {});
      toast.success("Override token issued — copied to clipboard. Tell the kiosk to retry.");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="contractor-overrides-page">
        <div>
          <h1 className="text-3xl font-bold text-navy-900 tracking-tight flex items-center gap-2">
            <ShieldAlert className="w-7 h-7 text-warning-600" /> Contractor sign-in overrides
          </h1>
          <p className="text-sm text-navy-500 mt-1">
            Pending blocked sign-in attempts. Approve with justification (≥ 20 chars).
            Tokens are single-use and expire in 30 minutes.
          </p>
        </div>

        {pending.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-navy-200 p-10 text-center">
            <CheckCircle2 className="w-10 h-10 mx-auto text-success-600 mb-2" />
            <div className="text-navy-700 font-medium">All clear — no blocked sign-ins right now.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {pending.map((req) => (
              <div key={req.id} className="rounded-2xl bg-white border border-navy-100 p-5 space-y-3" data-testid={`override-row-${req.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-navy-900">{req.contractor_name || req.contractor_id}</div>
                    <div className="text-xs text-navy-500">{req.contractor_company_name || "—"} · {req.site_name || req.site_id}</div>
                    <div className="text-xs text-navy-400 mt-1">Requested {new Date(req.requested_at).toLocaleString()}</div>
                  </div>
                  <Button
                    onClick={() => approve(req)}
                    disabled={busy === req.id}
                    className="bg-accent hover:bg-accent/90 text-white"
                    data-testid={`approve-override-${req.id}`}
                  >
                    {busy === req.id ? "Approving…" : "Approve override"}
                  </Button>
                </div>
                <ul className="space-y-1.5">
                  {(req.blockers || []).map((b, i) => (
                    <li key={i} className="text-sm flex items-start gap-2">
                      <span className="font-mono text-[11px] text-danger-700 bg-danger-50 px-1.5 py-0.5 rounded">{b.code}</span>
                      <span className="text-navy-700">{BLOCKER_LABELS[b.code] || b.code}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
